/**
 * # ConfirmLeave
 *
 * A component that provides confirmation before leaving a route.
 *
 * ## Props
 *
 * - `route: string | {path: string}`: The route to be confirmed before leaving.
 * - `router?: any`: The router object.
 * - `children?: React.ReactNode`: The children of the component.
 *
 * ## Usage
 *
 * ```tsx
 * import ConfirmLeave from './ConfirmLeave';
 *
 * const ExampleComponent = () => {
 *   return (
 *     <ConfirmLeave route="/example-route" router={router}>
 *       {({dismiss, confirmLeave}) => (
 *         <div>
 *           <button onClick={dismiss}>Dismiss</button>
 *           <button onClick={confirmLeave}>Confirm Leave</button>
 *         </div>
 *       )}
 *     </ConfirmLeave>
 *   );
 * };
 * ```
 */

import {useEffect, useRef, useState} from 'react';
import {withRouter} from 'react-router';

interface ConfirmLeaveProps {
  children?:
    | React.ReactNode
    | ((args: {dismiss: () => void; confirmLeave: () => void}) => React.ReactNode);
  route: string | {path: string};
  router?: any;
}

const ConfirmLeave = ({route, router, children}: ConfirmLeaveProps) => {
  // Dirty flag in a ref so the leave hook reads the CURRENT value at navigation
  // time — confirmLeave() disarms the guard synchronously before it re-navigates,
  // with no dependence on effect re-run ordering.
  const hasConfirmedLeaveRef = useRef(false);
  const [nextPath, setNextPath] = useState<string | null>(null);

  useEffect(() => {
    const removeLeaveHook = router.setRouteLeaveHook(route, (nextRoute) => {
      if (hasConfirmedLeaveRef.current) {
        return true;
      }
      setNextPath(nextRoute.pathname + nextRoute.search);
      return false;
    });
    return removeLeaveHook;
  }, [route, router]);

  const dismiss = () => setNextPath(null);
  const confirmLeave = () => {
    hasConfirmedLeaveRef.current = true;
    if (nextPath !== null) {
      router.pushState(null, nextPath);
    }
  };

  return nextPath !== null && typeof children === 'function'
    ? children({dismiss, confirmLeave})
    : null;
};

export default withRouter(ConfirmLeave);
